import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { placeLabel } from '../lib/placeLabel';
import { Calendar, MapPin, Users, Luggage, Clock, ArrowRight, CheckCircle2, Zap, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import PhoneInput from '../components/ui/phone-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatUkDateTime } from '../lib/date';
import { computeEndTime } from '../lib/time';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const libraries = ['places'];

const Booking = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const formCardRef = React.useRef(null);
  const { customer, isAuthenticated } = useCustomerAuth();
  const location = useLocation();
  const rebookPrefill = location.state?.prefill;

  // Auto-scroll to the top of the form whenever the step changes.
  React.useEffect(() => {
    if (!formCardRef.current) return;
    requestAnimationFrame(() => {
      const top = formCardRef.current.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }, [currentStep]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingId, setBookingId] = useState('');

  // Autocomplete refs
  const [pickupAutocomplete, setPickupAutocomplete] = useState(null);
  const [dropoffAutocomplete, setDropoffAutocomplete] = useState(null);
  const [startingAutocomplete, setStartingAutocomplete] = useState(null);
  const [stopoverAutocompletes, setStopoverAutocompletes] = useState([]);

  const [formData, setFormData] = useState({
    serviceType: '', // 'transfer' or 'as-directed'
    vehicle: '', // Selected vehicle
    vehicleName: '', // Vehicle display name
    pickupLocation: '',
    dropoffLocation: '',
    stopovers: [], // Array of stopover addresses
    startingLocation: '', // For as-directed
    pickupDate: '',
    pickupTime: '',
    returnRequired: 'no',
    returnDate: '',
    returnTime: '',
    hours: '2', // For as-directed service
    flightNumber: '', // Flight number for airport transfers
    passengers: '1',
    luggage: '1',
    childSeats: '0',
    specialRequests: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    // Booking on behalf of someone else
    bookingForOther: false,
    passengerName: '',
    passengerPhone: '',
    passengerEmail: '',
    // Corporate / business booking
    isCorporate: false,
    companyName: '',
    costReference: '',
    billingEmail: '',
    billingAddress: '',
    customerVatNumber: '',
    purchaseOrderNumber: '',
    invoiceContactName: ''
  });

  const [calculatedDistance, setCalculatedDistance] = useState(null);
  const [calculatedTime, setCalculatedTime] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Customer contact details — keep in sync whenever auth state resolves.
  useEffect(() => {
    if (isAuthenticated && customer) {
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || customer.firstName || '',
        lastName: prev.lastName || customer.lastName || '',
        email: prev.email || customer.email || '',
        phone: prev.phone || customer.phone || '',
      }));
    }
  }, [isAuthenticated, customer]);

  // Rebook prefill — runs once when arriving from "Book Again" on My Trips.
  // We deliberately jump to Step 3 (Journey) so the user immediately sees
  // their prior pickup, drop-off, vehicle and passenger counts ready to tweak.
  useEffect(() => {
    if (!rebookPrefill) return;
    setFormData((prev) => ({
      ...prev,
      serviceType: rebookPrefill.serviceType || prev.serviceType || 'transfer',
      pickupLocation: rebookPrefill.pickupLocation || prev.pickupLocation,
      dropoffLocation: rebookPrefill.dropoffLocation || prev.dropoffLocation,
      vehicle: rebookPrefill.vehicle || prev.vehicle,
      vehicleName: rebookPrefill.vehicleName || prev.vehicleName,
      passengers: rebookPrefill.passengers || prev.passengers,
      luggage: rebookPrefill.luggage || prev.luggage,
    }));
    setCurrentStep(3);
    toast.success('Previous trip details loaded — please confirm date & time');
    // Strip the state from history so a browser refresh doesn't re-fill.
    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/fleet`)
      .then((res) => setVehicles(res.data.vehicles || []))
      .catch(() => setVehicles([]));
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  // Calculate distance function with waypoints support
  const calculateDistanceAndTime = async () => {
    if (!window.google || !window.google.maps) return;
    
    setCalculating(true);
    try {
      // If we have stopovers, we need to use Directions Service for waypoints
      if (formData.stopovers && formData.stopovers.length > 0 && formData.stopovers.some(s => s.trim())) {
        const directionsService = new window.google.maps.DirectionsService();
        
        // Filter out empty stopovers
        const validStopovers = formData.stopovers.filter(s => s.trim());
        
        directionsService.route(
          {
            origin: formData.pickupLocation,
            destination: formData.dropoffLocation,
            waypoints: validStopovers.map(stop => ({
              location: stop,
              stopover: true
            })),
            travelMode: 'DRIVING',
            unitSystem: window.google.maps.UnitSystem.IMPERIAL,
          },
          (response, status) => {
            if (status === 'OK') {
              // Calculate total distance and duration across all legs
              let totalDistance = 0;
              let totalDuration = 0;
              
              response.routes[0].legs.forEach(leg => {
                totalDistance += leg.distance.value;
                totalDuration += leg.duration.value;
              });
              
              const distanceInMiles = totalDistance / 1609.34;
              const durationInMinutes = totalDuration / 60;
              
              setCalculatedDistance(distanceInMiles.toFixed(1));
              
              const hours = Math.floor(durationInMinutes / 60);
              const minutes = Math.round(durationInMinutes % 60);
              if (hours > 0) {
                setCalculatedTime(`${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`);
              } else {
                setCalculatedTime(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
              }
            }
            setCalculating(false);
          }
        );
      } else {
        // No stopovers, use simple Distance Matrix
        const service = new window.google.maps.DistanceMatrixService();
        
        service.getDistanceMatrix(
          {
            origins: [formData.pickupLocation],
            destinations: [formData.dropoffLocation],
            travelMode: 'DRIVING',
            unitSystem: window.google.maps.UnitSystem.IMPERIAL,
          },
          (response, status) => {
            if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
              const result = response.rows[0].elements[0];
              const distanceInMiles = result.distance.value / 1609.34;
              const durationInMinutes = result.duration.value / 60;
              
              setCalculatedDistance(distanceInMiles.toFixed(1));
              
              const hours = Math.floor(durationInMinutes / 60);
              const minutes = Math.round(durationInMinutes % 60);
              if (hours > 0) {
                setCalculatedTime(`${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`);
              } else {
                setCalculatedTime(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
              }
            }
            setCalculating(false);
          }
        );
      }
    } catch (error) {
      console.error('Distance calculation error:', error);
      setCalculating(false);
    }
  };

  // Auto-calculate when locations or stopovers change
  useEffect(() => {
    if (formData.serviceType !== 'transfer' || !formData.pickupLocation || !formData.dropoffLocation || !isLoaded) return undefined;
    const t = setTimeout(calculateDistanceAndTime, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pickupLocation, formData.dropoffLocation, formData.stopovers, isLoaded]);

  // Generate time slots with 15-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const displayTime = hour < 12 
          ? `${hour === 0 ? 12 : hour}:${String(minute).padStart(2, '0')} AM`
          : `${hour === 12 ? 12 : hour - 12}:${String(minute).padStart(2, '0')} PM`;
        slots.push({ value: time, label: displayTime });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const onPickupLoad = (autocomplete) => setPickupAutocomplete(autocomplete);
  const onDropoffLoad = (autocomplete) => setDropoffAutocomplete(autocomplete);
  const onStartingLoad = (autocomplete) => setStartingAutocomplete(autocomplete);

  const onPickupPlaceChanged = () => {
    if (pickupAutocomplete) {
      const place = pickupAutocomplete.getPlace();
      handleInputChange('pickupLocation', placeLabel(place, formData.pickupLocation));
    }
  };

  const onDropoffPlaceChanged = () => {
    if (dropoffAutocomplete) {
      const place = dropoffAutocomplete.getPlace();
      handleInputChange('dropoffLocation', placeLabel(place, formData.dropoffLocation));
    }
  };

  const onStartingPlaceChanged = () => {
    if (startingAutocomplete) {
      const place = startingAutocomplete.getPlace();
      handleInputChange('startingLocation', placeLabel(place, formData.startingLocation));
    }
  };

  const onStopoverLoad = (autocomplete, index) => {
    const newStopoverAutocompletes = [...stopoverAutocompletes];
    newStopoverAutocompletes[index] = autocomplete;
    setStopoverAutocompletes(newStopoverAutocompletes);
  };

  const onStopoverPlaceChanged = (index) => {
    if (stopoverAutocompletes[index]) {
      const place = stopoverAutocompletes[index].getPlace();
      const newStopovers = [...formData.stopovers];
      newStopovers[index] = placeLabel(place, newStopovers[index]);
      handleInputChange('stopovers', newStopovers);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.serviceType) {
      toast.error('Please select a service type');
      return;
    }
    if (currentStep === 2 && !formData.vehicle) {
      toast.error('Please select a vehicle');
      return;
    }
    if (currentStep === 3) {
      if (formData.serviceType === 'transfer') {
        if (!formData.pickupLocation || !formData.dropoffLocation || !formData.pickupDate || !formData.pickupTime) {
          toast.error('Please fill in all journey details');
          return;
        }
      } else {
        if (!formData.startingLocation || !formData.pickupDate || !formData.pickupTime || !formData.hours) {
          toast.error('Please fill in all service details');
          return;
        }
      }
    }
    if (currentStep === 5) {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
        toast.error('Please fill in all contact details');
        return;
      }
      if (formData.bookingForOther && (!formData.passengerName || !formData.passengerPhone)) {
        toast.error('Please add the passenger name and phone');
        return;
      }
      if (formData.isCorporate && !formData.companyName) {
        toast.error('Please add the company name');
        return;
      }
    }
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Prepare data based on service type
      const submitData = { ...formData };
      
      // Find selected vehicle name
      const selectedVehicle = vehicles.find(v => v.id === formData.vehicle);
      
      if (formData.serviceType === 'as-directed') {
        submitData.pickupLocation = formData.startingLocation;
        submitData.dropoffLocation = `As Directed - ${formData.hours} hours (${parseInt(formData.hours) * 15} miles allowance)`;
        submitData.serviceType = 'As Directed Service';
        submitData.asDirectedHours = parseFloat(formData.hours);
      } else {
        submitData.serviceType = 'Transfer';
      }
      
      // Add calculated values and vehicle name
      submitData.vehicleName = selectedVehicle ? selectedVehicle.name : '';
      submitData.calculatedDistance = calculatedDistance;
      submitData.calculatedTime = calculatedTime;
      
      const response = await axios.post(`${API}/bookings`, submitData);
      
      setBookingId(response.data.bookingId);
      setBookingConfirmed(true);
      
      if (response.data.emailSent) {
        toast.success('Quote request submitted! Check your email for confirmation.');
      } else {
        toast.success('Quote request submitted successfully!');
        toast.info('You can expect a call/WhatsApp shortly.');
      }
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    console.error('Google Maps load error:', loadError);
  }

  if (!isLoaded && !loadError) {
    return (
      <div className="min-h-screen bg-white text-gray-900 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking form...</p>
        </div>
      </div>
    );
  }

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-white text-gray-900 pt-20 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="mb-8">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-black" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-black">Quote Request Received!</h1>
            <p className="text-xl text-gray-600 mb-2">Booking Reference: <span className="font-mono font-bold text-black">{bookingId}</span></p>
            <p className="text-gray-600">
              Thank you for choosing Obsidian Chauffeurs. We've received your quote request and we're on it - you'll hear from us as soon as possible with a detailed quotation.
            </p>
          </div>
          
          <Card className="bg-gray-50 border-gray-200 p-8 text-left mb-8">
            <h2 className="text-xl font-bold mb-4 text-black">What Happens Next?</h2>
            <div className="space-y-4">
              {[
                { num: 1, title: 'Confirmation Email Sent', desc: 'Check your inbox for booking confirmation' },
                { num: 2, title: "We'll Send Quotation", desc: 'Personalised quote as soon as possible' },
                { num: 3, title: 'Accept Quote', desc: 'Review and accept our quotation' },
                { num: 4, title: 'Booking Confirmed', desc: 'Confirmation and driver details sent' }
              ].map((step) => (
                <div key={step.num} className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">{step.num}</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1 text-black">{step.title}</div>
                    <div className="text-sm text-gray-600">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => {
                setBookingConfirmed(false);
                setCurrentStep(1);
                setFormData({
                  serviceType: '',
                  pickupLocation: '',
                  dropoffLocation: '',
                  startingLocation: '',
                  pickupDate: '',
                  pickupTime: '',
                  returnRequired: 'no',
                  returnDate: '',
                  returnTime: '',
                  hours: '2',
                  passengers: '1',
                  luggage: '1',
                  childSeats: '0',
                  specialRequests: '',
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  company: ''
                });
              }}
              variant="outline"
              className="border-2 border-black text-black hover:bg-black hover:text-white"
            >
              Make Another Booking
            </Button>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-black text-white hover:bg-gray-800"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 pt-20">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-black">Request a Quote</h1>
          <p className="text-xl text-gray-600">
            Tell us about your journey and we'll provide a personalised quotation
          </p>
        </div>
      </section>

      {/* Progress Indicator */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4, 5].map((step) => (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                    currentStep >= step ? 'bg-black text-white' : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {step}
                </div>
                <div className={`text-xs mt-2 hidden sm:block ${currentStep >= step ? 'text-black font-medium' : 'text-gray-400'}`}>
                  {step === 1 && 'Service'}
                  {step === 2 && 'Vehicle'}
                  {step === 3 && 'Journey'}
                  {step === 4 && 'Details'}
                  {step === 5 && 'Contact'}
                </div>
              </div>
              {step < 5 && (
                <div className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${
                  currentStep > step ? 'bg-black' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Form */}
      <section className="py-8 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Card ref={formCardRef} className="bg-white border-gray-200 p-8 lg:p-12 shadow-lg">
            {/* Step 1: Service Type - Transfer or As Directed */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold mb-2 text-black">Select Your Service</h2>
                  <p className="text-gray-600">Choose the type of service you need</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <button
                    onClick={() => handleInputChange('serviceType', 'transfer')}
                    className={`p-8 rounded-lg border-2 text-left transition-all duration-300 ${
                      formData.serviceType === 'transfer'
                        ? 'border-black bg-black text-white'
                        : 'border-gray-200 hover:border-black hover:bg-gray-50'
                    }`}
                  >
                    <h3 className="text-2xl font-bold mb-3">Transfer</h3>
                    <p className={`text-sm ${formData.serviceType === 'transfer' ? 'text-white/80' : 'text-gray-600'}`}>
                      Point-to-point journey with optional return
                    </p>
                    <ul className={`mt-4 space-y-2 text-sm ${formData.serviceType === 'transfer' ? 'text-white/90' : 'text-gray-700'}`}>
                      <li>• Airport transfers</li>
                      <li>• One-way or return</li>
                      <li>• Fixed route</li>
                    </ul>
                  </button>

                  <button
                    onClick={() => handleInputChange('serviceType', 'as-directed')}
                    className={`p-8 rounded-lg border-2 text-left transition-all duration-300 ${
                      formData.serviceType === 'as-directed'
                        ? 'border-black bg-black text-white'
                        : 'border-gray-200 hover:border-black hover:bg-gray-50'
                    }`}
                  >
                    <h3 className="text-2xl font-bold mb-3">As Directed Service</h3>
                    <p className={`text-sm ${formData.serviceType === 'as-directed' ? 'text-white/80' : 'text-gray-600'}`}>
                      Hourly service with flexible routing
                    </p>
                    <ul className={`mt-4 space-y-2 text-sm ${formData.serviceType === 'as-directed' ? 'text-white/90' : 'text-gray-700'}`}>
                      <li>• Hourly bookings</li>
                      <li>• 15 miles per hour allowance</li>
                      <li>• Flexible destinations</li>
                    </ul>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Vehicle Selection */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold mb-2 text-black">Select Your Vehicle</h2>
                  <p className="text-gray-600">Choose the perfect vehicle for your journey</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {vehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      onClick={() => handleInputChange('vehicle', vehicle.id)}
                      className={`rounded-lg border-2 text-left transition-all duration-300 overflow-hidden ${
                        formData.vehicle === vehicle.id
                          ? 'border-black bg-black text-white shadow-lg scale-105'
                          : 'border-gray-200 hover:border-black hover:shadow-md bg-white'
                      }`}
                    >
                      {/* Vehicle Image */}
                      <div className="w-full h-48 overflow-hidden bg-gray-100">
                        {vehicle.image ? (
                          <img 
                            src={vehicle.image} 
                            alt={vehicle.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <Car className="h-12 w-12" />
                          </div>
                        )}
                      </div>
                      
                      {/* Vehicle Details */}
                      <div className="p-6">
                        <div className="mb-4">
                          <h3 className="text-lg font-bold mb-1 flex items-center gap-1.5">
                            {vehicle.name}
                            {vehicle.isElectric && <Zap className={`h-4 w-4 ${formData.vehicle === vehicle.id ? 'text-emerald-300' : 'text-emerald-600'}`} />}
                          </h3>
                          <p className={`text-xs font-semibold ${formData.vehicle === vehicle.id ? 'text-white/80' : 'text-gray-500'}`}>
                            {vehicle.category}
                          </p>
                        </div>
                        <p className={`text-sm mb-4 ${formData.vehicle === vehicle.id ? 'text-white/90' : 'text-gray-600'}`}>
                          {vehicle.description}
                        </p>
                        <div className={`flex items-center justify-between text-sm ${formData.vehicle === vehicle.id ? 'text-white' : 'text-gray-700'}`}>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span className="font-semibold">{vehicle.passengers}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Luggage className="h-4 w-4" />
                            <span className="font-semibold">{vehicle.luggage}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Journey Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold mb-2 text-black">
                    {formData.serviceType === 'transfer' ? 'Journey Details' : 'Service Details'}
                  </h2>
                  <p className="text-gray-600">Tell us about your travel plans</p>
                </div>

                {formData.serviceType === 'transfer' ? (
                  // Transfer Form
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label className="text-gray-900 mb-2 font-medium">Pickup Location *</Label>
                      {isLoaded && !loadError ? (
                        <Autocomplete
                          onLoad={onPickupLoad}
                          onPlaceChanged={onPickupPlaceChanged}
                          options={{ componentRestrictions: { country: 'uk' } }}
                        >
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                            <Input
                              value={formData.pickupLocation}
                              onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
                              placeholder="Start typing address..."
                              className="pl-10 bg-white border-gray-300 text-gray-900"
                            />
                          </div>
                        </Autocomplete>
                      ) : (
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            value={formData.pickupLocation}
                            onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
                            placeholder="Enter pickup address"
                            className="pl-10 bg-white border-gray-300 text-gray-900"
                          />
                        </div>
                      )}
                    </div>

                    {/* Stopovers Section - positioned between pickup and dropoff */}
                    <div className="md:col-span-2">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-gray-900 font-medium">Stopovers (Optional)</Label>
                        <Button
                          type="button"
                          onClick={() => {
                            const newStopovers = [...formData.stopovers, ''];
                            handleInputChange('stopovers', newStopovers);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          + Add Stopover
                        </Button>
                      </div>
                      
                      {formData.stopovers.length > 0 && (
                        <div className="space-y-3">
                          {formData.stopovers.map((stopover, index) => (
                            <div key={index} className="flex gap-2">
                              {isLoaded && !loadError ? (
                                <Autocomplete
                                  onLoad={(autocomplete) => onStopoverLoad(autocomplete, index)}
                                  onPlaceChanged={() => onStopoverPlaceChanged(index)}
                                  options={{ componentRestrictions: { country: 'uk' } }}
                                  className="flex-1 min-w-0"
                                >
                                  <div className="relative flex-1">
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                                    <Input
                                      value={stopover}
                                      onChange={(e) => {
                                        const newStopovers = [...formData.stopovers];
                                        newStopovers[index] = e.target.value;
                                        handleInputChange('stopovers', newStopovers);
                                      }}
                                      placeholder={`Stopover ${index + 1} - Start typing address...`}
                                      className="pl-10 bg-white border-gray-300 text-gray-900"
                                      data-testid={`transfer-stopover-${index}`}
                                    />
                                  </div>
                                </Autocomplete>
                              ) : (
                                <div className="relative flex-1">
                                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                  <Input
                                    value={stopover}
                                    onChange={(e) => {
                                      const newStopovers = [...formData.stopovers];
                                      newStopovers[index] = e.target.value;
                                      handleInputChange('stopovers', newStopovers);
                                    }}
                                    placeholder={`Stopover ${index + 1} address`}
                                    className="pl-10 bg-white border-gray-300 text-gray-900"
                                    data-testid={`transfer-stopover-${index}`}
                                  />
                                </div>
                              )}
                              <Button
                                type="button"
                                onClick={() => {
                                  const newStopovers = formData.stopovers.filter((_, i) => i !== index);
                                  handleInputChange('stopovers', newStopovers);
                                  // Clean up autocomplete ref
                                  const newAutocompletes = stopoverAutocompletes.filter((_, i) => i !== index);
                                  setStopoverAutocompletes(newAutocompletes);
                                }}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`transfer-remove-stopover-${index}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Add any stops you'd like to make during your journey</p>
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-gray-900 mb-2 font-medium">Drop-off Location *</Label>
                      {isLoaded && !loadError ? (
                        <Autocomplete
                          onLoad={onDropoffLoad}
                          onPlaceChanged={onDropoffPlaceChanged}
                          options={{ componentRestrictions: { country: 'uk' } }}
                        >
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                            <Input
                              value={formData.dropoffLocation}
                              onChange={(e) => handleInputChange('dropoffLocation', e.target.value)}
                              placeholder="Start typing destination..."
                              className="pl-10 bg-white border-gray-300 text-gray-900"
                            />
                          </div>
                        </Autocomplete>
                      ) : (
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            value={formData.dropoffLocation}
                            onChange={(e) => handleInputChange('dropoffLocation', e.target.value)}
                            placeholder="Enter destination"
                            className="pl-10 bg-white border-gray-300 text-gray-900"
                          />
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-gray-900 mb-2 font-medium">Flight Number (Optional)</Label>
                      <Input
                        value={formData.flightNumber}
                        onChange={(e) => handleInputChange('flightNumber', e.target.value)}
                        placeholder="e.g., BA123, EZY456"
                        className="bg-white border-gray-300 text-gray-900"
                      />
                      <p className="text-xs text-gray-500 mt-1">For airport transfers, we'll monitor your flight status</p>
                    </div>

                    {/* Distance and Time Display */}
                    {(calculatedDistance || calculating) && (
                      <div className="md:col-span-2 bg-gray-50 border border-gray-200 rounded-lg p-6">
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Journey Distance</p>
                            {calculating ? (
                              <p className="text-2xl font-bold text-gray-400">Calculating...</p>
                            ) : (
                              <p className="text-3xl font-bold text-black">{calculatedDistance} miles</p>
                            )}
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Estimated Travel Time</p>
                            {calculating ? (
                              <p className="text-2xl font-bold text-gray-400">Calculating...</p>
                            ) : (
                              <p className="text-3xl font-bold text-black">{calculatedTime}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-gray-900 mb-2 font-medium">Pickup Date *</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                        <Input
                          type="date"
                          value={formData.pickupDate}
                          onChange={(e) => handleInputChange('pickupDate', e.target.value)}
                          className="pl-10 bg-white border-gray-300 text-gray-900"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-900 mb-2 font-medium">Pickup Time *</Label>
                      <Select value={formData.pickupTime} onValueChange={(value) => handleInputChange('pickupTime', value)}>
                        <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                          <Clock className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-gray-900 mb-2 font-medium">Return Journey Required?</Label>
                      <div className="flex gap-4">
                        <button
                          onClick={() => handleInputChange('returnRequired', 'no')}
                          className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                            formData.returnRequired === 'no'
                              ? 'border-black bg-black text-white'
                              : 'border-gray-200 hover:border-black'
                          }`}
                        >
                          No
                        </button>
                        <button
                          onClick={() => handleInputChange('returnRequired', 'yes')}
                          className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                            formData.returnRequired === 'yes'
                              ? 'border-black bg-black text-white'
                              : 'border-gray-200 hover:border-black'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                    </div>

                    {formData.returnRequired === 'yes' && (
                      <>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Return Date</Label>
                          <Input
                            type="date"
                            value={formData.returnDate}
                            onChange={(e) => handleInputChange('returnDate', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            min={formData.pickupDate}
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Return Time</Label>
                          <Select value={formData.returnTime} onValueChange={(value) => handleInputChange('returnTime', value)}>
                            <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                              <Clock className="h-4 w-4 mr-2" />
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {timeSlots.map((slot) => (
                                <SelectItem key={slot.value} value={slot.value}>
                                  {slot.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  // As Directed Form
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label className="text-gray-900 mb-2 font-medium">Starting Location *</Label>
                      {isLoaded && !loadError ? (
                        <Autocomplete
                          onLoad={onStartingLoad}
                          onPlaceChanged={onStartingPlaceChanged}
                          options={{ componentRestrictions: { country: 'uk' } }}
                        >
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                            <Input
                              value={formData.startingLocation}
                              onChange={(e) => handleInputChange('startingLocation', e.target.value)}
                              placeholder="Start typing address..."
                              className="pl-10 bg-white border-gray-300 text-gray-900"
                            />
                          </div>
                        </Autocomplete>
                      ) : (
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            value={formData.startingLocation}
                            onChange={(e) => handleInputChange('startingLocation', e.target.value)}
                            placeholder="Enter starting address"
                            className="pl-10 bg-white border-gray-300 text-gray-900"
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-gray-900 mb-2 font-medium">Service Date *</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                        <Input
                          type="date"
                          value={formData.pickupDate}
                          onChange={(e) => handleInputChange('pickupDate', e.target.value)}
                          className="pl-10 bg-white border-gray-300 text-gray-900"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-900 mb-2 font-medium">Start Time *</Label>
                      <Select value={formData.pickupTime} onValueChange={(value) => handleInputChange('pickupTime', value)}>
                        <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                          <Clock className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-gray-900 mb-2 font-medium">Number of Hours * (15 miles per hour allowance)</Label>
                      <Select value={formData.hours} onValueChange={(value) => handleInputChange('hours', value)}>
                        <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((h) => (
                            <SelectItem key={h} value={String(h)}>
                              {h} hours ({h * 15} miles allowance)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-2">
                        Each hour includes a 15-mile allowance. Additional mileage charged separately.
                      </p>
                    </div>

                    {/* Stopovers Section - As Directed multi-stop */}
                    <div className="md:col-span-2">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-gray-900 font-medium">Stops / Waypoints (Optional)</Label>
                        <Button
                          type="button"
                          onClick={() => {
                            const newStopovers = [...formData.stopovers, ''];
                            handleInputChange('stopovers', newStopovers);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          data-testid="asdirected-add-stopover-btn"
                        >
                          + Add Stop
                        </Button>
                      </div>

                      {formData.stopovers.length > 0 && (
                        <div className="space-y-3">
                          {formData.stopovers.map((stopover, index) => (
                            <div key={index} className="flex gap-2">
                              {isLoaded && !loadError ? (
                                <Autocomplete
                                  onLoad={(autocomplete) => onStopoverLoad(autocomplete, index)}
                                  onPlaceChanged={() => onStopoverPlaceChanged(index)}
                                  options={{ componentRestrictions: { country: 'uk' } }}
                                  className="flex-1 min-w-0"
                                >
                                  <div className="relative flex-1">
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                                    <Input
                                      value={stopover}
                                      onChange={(e) => {
                                        const newStopovers = [...formData.stopovers];
                                        newStopovers[index] = e.target.value;
                                        handleInputChange('stopovers', newStopovers);
                                      }}
                                      placeholder={`Stop ${index + 1} - Start typing address...`}
                                      className="pl-10 bg-white border-gray-300 text-gray-900"
                                      data-testid={`asdirected-stopover-${index}`}
                                    />
                                  </div>
                                </Autocomplete>
                              ) : (
                                <div className="relative flex-1">
                                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                  <Input
                                    value={stopover}
                                    onChange={(e) => {
                                      const newStopovers = [...formData.stopovers];
                                      newStopovers[index] = e.target.value;
                                      handleInputChange('stopovers', newStopovers);
                                    }}
                                    placeholder={`Stop ${index + 1} address`}
                                    className="pl-10 bg-white border-gray-300 text-gray-900"
                                    data-testid={`asdirected-stopover-${index}`}
                                  />
                                </div>
                              )}
                              <Button
                                type="button"
                                onClick={() => {
                                  const newStopovers = formData.stopovers.filter((_, i) => i !== index);
                                  handleInputChange('stopovers', newStopovers);
                                  const newAutocompletes = stopoverAutocompletes.filter((_, i) => i !== index);
                                  setStopoverAutocompletes(newAutocompletes);
                                }}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`asdirected-remove-stopover-${index}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Add any stops the chauffeur should make during your hire</p>
                    </div>

                  </div>
                )}
              </div>
            )}

            {/* Step 4: Passenger Details */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold mb-2 text-black">Passenger Details</h2>
                  <p className="text-gray-600">Help us prepare for your journey</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Number of Passengers</Label>
                    <Select value={formData.passengers} onValueChange={(value) => handleInputChange('passengers', value)}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                        <Users className="h-4 w-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                          <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Pieces of Luggage</Label>
                    <Select value={formData.luggage} onValueChange={(value) => handleInputChange('luggage', value)}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                        <Luggage className="h-4 w-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Child Seats Required</Label>
                    <Select value={formData.childSeats} onValueChange={(value) => handleInputChange('childSeats', value)}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3].map((num) => (
                          <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-900 mb-2 font-medium">Special Requests or Requirements</Label>
                  <Textarea
                    value={formData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    placeholder="Any special requirements, accessibility needs, or preferences..."
                    rows={4}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Contact Information */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold mb-2 text-black">Contact Information</h2>
                  <p className="text-gray-600">How can we reach you?</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">First Name *</Label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="John"
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Last Name *</Label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Smith"
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Email Address *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john.smith@example.com"
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 mb-2 font-medium">Phone Number *</Label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(val) => handleInputChange('phone', val)}
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>

                  <div className="md:col-span-2 pt-2 space-y-3">
                    {/* Toggle 1 — booking for someone else */}
                    <label
                      className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-gray-300 bg-white cursor-pointer"
                      data-testid="toggle-for-other"
                    >
                      <input
                        type="checkbox"
                        checked={formData.bookingForOther}
                        onChange={(e) => handleInputChange('bookingForOther', e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                      />
                      <span className="flex-1">
                        <span className="block text-sm font-semibold text-black">Booking for someone else</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          We'll send updates to you, but the chauffeur will collect the passenger you list below.
                        </span>
                      </span>
                    </label>

                    {formData.bookingForOther && (
                      <div className="grid md:grid-cols-3 gap-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Passenger Name *</Label>
                          <Input
                            value={formData.passengerName}
                            onChange={(e) => handleInputChange('passengerName', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="passenger-name"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Passenger Phone *</Label>
                          <PhoneInput
                            value={formData.passengerPhone}
                            onChange={(v) => handleInputChange('passengerPhone', v)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="passenger-phone"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Passenger Email</Label>
                          <Input
                            type="email"
                            value={formData.passengerEmail}
                            onChange={(e) => handleInputChange('passengerEmail', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="passenger-email"
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    )}

                    {/* Toggle 2 — corporate / business booking */}
                    <label
                      className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-gray-300 bg-white cursor-pointer"
                      data-testid="toggle-corporate"
                    >
                      <input
                        type="checkbox"
                        checked={formData.isCorporate}
                        onChange={(e) => handleInputChange('isCorporate', e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                      />
                      <span className="flex-1">
                        <span className="block text-sm font-semibold text-black">For business / corporate booking</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          We'll bill the company and reference your cost code on the invoice.
                        </span>
                      </span>
                    </label>

                    {formData.isCorporate && (
                      <div className="grid md:grid-cols-3 gap-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Company Name *</Label>
                          <Input
                            value={formData.companyName}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="company-name"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Cost Reference</Label>
                          <Input
                            value={formData.costReference}
                            onChange={(e) => handleInputChange('costReference', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="cost-reference"
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Purchase Order (PO) Number</Label>
                          <Input
                            value={formData.purchaseOrderNumber}
                            onChange={(e) => handleInputChange('purchaseOrderNumber', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="purchase-order-number"
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Invoice Contact Name</Label>
                          <Input
                            value={formData.invoiceContactName}
                            onChange={(e) => handleInputChange('invoiceContactName', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="invoice-contact-name"
                            placeholder="e.g. Accounts team / Finance manager"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Billing Email</Label>
                          <Input
                            type="email"
                            value={formData.billingEmail}
                            onChange={(e) => handleInputChange('billingEmail', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="billing-email"
                            placeholder="accounts@company.com (defaults to your email)"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-gray-900 mb-2 font-medium">Billing Address</Label>
                          <Textarea
                            rows={3}
                            value={formData.billingAddress}
                            onChange={(e) => handleInputChange('billingAddress', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="billing-address"
                            placeholder="Full company address — used on your invoice"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-900 mb-2 font-medium">Company VAT Number</Label>
                          <Input
                            value={formData.customerVatNumber}
                            onChange={(e) => handleInputChange('customerVatNumber', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            data-testid="customer-vat-number"
                            placeholder="GB123456789 (optional)"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Booking Summary */}
                <Card className="bg-gray-50 border-gray-200 p-6 mt-8">
                  <h3 className="font-bold text-xl mb-4 text-black">Booking Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service:</span>
                      <span className="font-semibold text-black">
                        {formData.serviceType === 'transfer' ? 'Transfer' : 'As Directed Service'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vehicle:</span>
                      <span className="font-semibold text-black">
                        {vehicles.find(v => v.id === formData.vehicle)?.name || 'N/A'}
                      </span>
                    </div>
                    {formData.serviceType === 'transfer' ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pickup:</span>
                          <span className="font-semibold text-black">{formData.pickupLocation || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Drop-off:</span>
                          <span className="font-semibold text-black">{formData.dropoffLocation || 'N/A'}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Starting:</span>
                          <span className="font-semibold text-black">{formData.startingLocation || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Duration:</span>
                          <span className="font-semibold text-black">{formData.hours} hours ({parseInt(formData.hours) * 15} miles)</span>
                        </div>
                        {formData.pickupTime && formData.hours && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Window:</span>
                            <span className="font-semibold text-black">{formData.pickupTime} – {computeEndTime(formData.pickupTime, formData.hours)}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date & Time:</span>
                      <span className="font-semibold text-black">{formatUkDateTime(formData.pickupDate, formData.pickupTime)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Passengers:</span>
                      <span className="font-semibold text-black">{formData.passengers}</span>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {currentStep > 1 && (
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 border-2 border-gray-300 text-gray-700 hover:bg-gray-100"
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="flex-1 bg-black text-white hover:bg-gray-800"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : currentStep === 5 ? 'Submit Quote Request' : 'Continue'}
                {currentStep < 4 && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Static Help Bar at Bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 py-4 px-6 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm">
          <p className="text-gray-600 font-medium">Need help with your booking?</p>
          <a 
            href="tel:03333357733" 
            className="flex items-center gap-2 text-black hover:text-gray-700 font-semibold"
          >
            <span className="text-lg">📞</span>
            0333 335 7733
          </a>
          <a 
            href="https://wa.me/443333357733" 
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-black hover:text-gray-700 font-semibold"
          >
            <span className="text-lg">💬</span>
            WhatsApp
          </a>
          <a 
            href="mailto:info@obsidianchauffeurs.co.uk" 
            className="flex items-center gap-2 text-black hover:text-gray-700 font-semibold"
          >
            <span className="text-lg">✉️</span>
            info@obsidianchauffeurs.co.uk
          </a>
        </div>
      </div>
    </div>
  );
};

export default Booking;